import { openToastr } from '@Common/handlers/services';
import { RootState } from '@Common/models/redux/store';
import {
  Tooltip,
  FormControl,
  TextField,
  CircularProgress,
  Autocomplete,
  InputLabel,
  Paper,
  PaperProps,
} from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { generateCitations } from 'src/researcher/handlers/api';
import {
  replaceTempLoadersWithCitedData,
  setupTempLoadersForCitations,
} from 'src/researcher/utils';
import { resetOverlayContainer } from 'src/researcher/handlers/grammar-check';
import citationGeneratorTrack from '../handlers/track';
import {
  changeCitationStyle,
  rebuildCitationNumbers,
  updateCitedData,
} from '../reducers/actionCreators';
import { ICitationStyle, CitationTabTooltips } from '../types';
import { TriggeringActions } from '../types/names';
import { convertCitedDataResponse, useCitationStyles } from '../utils';

const useSelectStyles = makeStyles((theme) => ({
  formControl: {
    width: '100%',
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
      paddingLeft: 8,
      paddingBottom: 4,
      paddingTop: 8,
      '& .MuiAutocomplete-input:first-child': {
        fontSize: 12,
        padding: '9px 0 2px 0',
        fontWeight: 600,
      },
    },
  },
  label: {
    fontSize: 10,
    color: theme.palette.text.secondary,
    left: 7,
    lineHeight: 1.3,
    transform: 'none',
    marginTop: 2,
    '&.Mui-focused': {
      color: 'red',
    },
  },
  paper: {
    paddingRight: 2,
    fontSize: 12,
    fontWeight: 400,
    boxShadow: '0px 2px 5px 0px #00000026',
    '&::-webkit-scrollbar-track-piece': {
      backgroundColor: 'transparent !important',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 8,
    },
    '& > ul > li[aria-selected="true"]': {
      fontSize: 12,
      fontWeight: 600,
    },
  },
}));

const CustomPaper = (props: PaperProps) => <Paper {...props} elevation={3} />;

const formatNameFromOption = (option: ICitationStyle) => {
  if (option.nameShort) {
    return `${option.nameShort}: ${option.name}`;
  }

  return option.name;
};

export default function CitationSelect() {
  const router = useRouter();
  const isCowriter = router.pathname.includes('/co-writer');
  const citationListFromPage = isCowriter ? 'citationsFromCowriter' : 'citationList';
  const citations = useSelector((state: RootState) => state.ctgr[citationListFromPage]);
  const citationStyle = useSelector((state: RootState) => state.ctgr.citationStyle);
  const dispatch = useDispatch();
  const classes = useSelectStyles();
  const [openTooltip, setOpenTooltip] = useState(false);
  const [openCitationSelect, setOpenCitationSelect] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [autocompleteValue, setAutocompleteValue] = useState<ICitationStyle>();
  const [citationStyles, isLoadingStyles] = useCitationStyles();
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    const current = citationStyles.find((cs) => cs.id === citationStyle);

    if (current) {
      setAutocompleteValue(current);
      setInputValue(formatNameFromOption(current));
      return;
    }

    const displayName = citationStyle
      .split('-')
      .map((s) => `${s[0].toUpperCase()}${s.substring(1)}`)
      .join(' ');
    setAutocompleteValue({ id: citationStyle, name: displayName });
    setInputValue(displayName);
  }, [citationStyle, citationStyles]);

  return (
    <Tooltip
      title={CitationTabTooltips.TabControlsCitationStyle}
      open={openTooltip}
      arrow
      placement="top"
    >
      <div
        style={{ flex: 1 }}
        onMouseEnter={() => {
          if (!openCitationSelect) {
            setOpenTooltip(true);
          }
        }}
        onMouseLeave={() => setOpenTooltip(false)}
      >
        <FormControl className={classes.formControl}>
          <InputLabel className={classes.label}>
            <span>Citation Style</span>
          </InputLabel>

          <Autocomplete
            disableClearable
            key={autocompleteValue?.name}
            open={openCitationSelect}
            classes={{
              paper: classes.paper,
            }}
            onOpen={() => {
              setOpenCitationSelect(true);
              setOpenTooltip(false);
              setInputValue('');
            }}
            onClose={() => {
              setOpenCitationSelect(false);
              setOpenTooltip(false);
            }}
            options={citationStyles}
            noOptionsText="No matching styles"
            disabled={isLoadingStyles || isLoading}
            getOptionLabel={(option) => formatNameFromOption(option)}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            value={autocompleteValue}
            loading={isLoadingStyles || isLoading}
            inputValue={inputValue}
            onInputChange={(event, newInputValue, reason) => {
              if (event) {
                setInputValue(newInputValue);
              }
            }}
            onChange={(e, val) => {
              if (val) {
                dispatch(changeCitationStyle(val.id));
                citationGeneratorTrack.userChangedCitationStyle(
                  val.id,
                  TriggeringActions.CITATION_HEADER
                );

                if (citations.length) {
                  // for co-writer gc
                  // resetting and repositioning gc errors due to change in length of in-text citations
                  resetOverlayContainer();
                  setupTempLoadersForCitations(citations);
                  setIsLoading(true);
                  generateCitations({ style: val.id, citations })
                    .then((response) => {
                      if (val.id !== response.style) dispatch(changeCitationStyle(response.style));
                      const citedData = convertCitedDataResponse(response);
                      dispatch(updateCitedData(citedData));
                      dispatch(rebuildCitationNumbers());
                    })
                    .catch(() => {
                      openToastr('Unable to use selected citation style!');
                      // TODO: track this as event
                    })
                    .finally(() => {
                      setIsLoading(false);
                      replaceTempLoadersWithCitedData(citations);
                    });
                }
              }
            }}
            PaperComponent={CustomPaper}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Search for a citation style"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingStyles || isLoading ? <CircularProgress size={15} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </FormControl>
      </div>
    </Tooltip>
  );
}
